/**
 * Live markup for the report currently open in the workspace (Phase 7). Reloads on any
 * realtime change to report_annotations for this report - simple full refetch, fine at the
 * scale of a handful to a few dozen annotations per report.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchAnnotationsForReport, subscribeReportAnnotations } from './queries';
import type { AnnotationRow } from './types';

export function useReportAnnotations(reportId: string | null) {
  const [annotations, setAnnotations] = useState<AnnotationRow[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const load = useCallback(async () => {
    if (!reportId) return;
    setStatus('loading');
    try {
      const rows = await fetchAnnotationsForReport(reportId);
      setAnnotations(rows);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [reportId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!reportId) return;
    return subscribeReportAnnotations(reportId, () => void load());
  }, [reportId, load]);

  return { annotations, status, reload: load };
}
